import { inject, ref } from 'vue'
import {
  CustomNotificationService,
  IProposalCustomerCustomNotificationSettings,
} from '../services/CustomNotificationService'

import { pick } from 'lodash'
import { REDIRECTS_TYPES } from '../const'
import { useNotify } from './useNotify'

export const defaultCustomNotificationSettings = {
  documentReceivedTemplateId: 'default',
  documentSignedTemplateId: 'default',
  documentSignedTemplateIdForTeam: 'default',
  documentExpiryReminderTemplateId: 'default',

  enableDocumentReceiveNotification: true,
  enableCCRecipientNotification: true,
  enableDocumentSignedNotification: true,
  enableDocumentSignedForTeamNotification: true,
  enableDocumentExpiry: false,
  enableDocumentExpiryReminderNotification: false,
  enableAutoSentInvoice: false,
  enableDirectPayment: false,
  documentSignedSubject: '[{{location.name}}] {{document.name}} signed',
  documentReceivedSubject: '[{{location.name}}] {{document.name}} received',
  documentSignedForTeamSubject: '[{{location.name}}] {{document.name}} signed',
  documentExpiryReminderSubject:
    '[{{location.name}}] {{document.name}} reminder',
  documentViewedSubject: '[{{location.name}}] {{document.name}} viewed',
  documentSignedEmail: '',
  documentSignedEmailFromName: '',
  documentReceivedEmail: '',
  documentReceivedEmailFromName: '',
  overrideSignedEmailSender: false,
  overrideReceivedEmailSender: false,
  overrideSignedForTeamEmailSender: false,
  documentSignedForTeamEmail: '',
  documentSignedForTeamEmailFromName: '',
  ccRecipients: [],
  documentSignedDefaultTemplateId: '',
  documentReceivedDefaultTemplateId: '',
  documentExpiryReminderDefaultTemplateId: '',

  documentExpiryDays: 0,
  enableDocumentViewedNotification: false,
  documentViewedTemplateId: '',
  enableDocumentSignatureDraw: true,
  enableDocumentSignatureType: true,

  enableDocumentRedirectUrl: false,
  documentRedirectUrl: '',
  documentRedirectType: REDIRECTS_TYPES.NEW_TAB,

  enableDocumentReceiveNotificationViaEmail: true,
  enableDocumentReceiveNotificationViaSMS: false,
  documentReceivedSMSTemplateId: '',
  enableDocumentSignedNotificationViaEmail: true,
  enableDocumentSignedNotificationViaSMS: false,
  documentSignedSMSTemplateId: '',
} as Omit<IProposalCustomerCustomNotificationSettings, 'locationId'>

const loading = ref(false)
const error = ref(null)
const notificationSetting = ref(defaultCustomNotificationSettings)

export const useCustomNotificationSettings = (altId?: string) => {
  const route = inject<any>('route')
  const locationId = (route?.params?.location_id as string) || (altId as string)
  const { handleError, handleInfo } = useNotify()

  const sanitizeSettingsData = (data: Record<string, any>) => {
    return pick(data, [
      'documentReceivedTemplateId',
      'documentReceivedDefaultTemplateId',
      'documentSignedDefaultTemplateId',
      'documentSignedTemplateId',
      'documentSignedTemplateIdForTeam',
      'enableDocumentReceiveNotification',
      'enableCCRecipientNotification',
      'enableDocumentSignedForTeamNotification',
      'enableDocumentSignedNotification',
      'documentReceivedSubject',
      'documentSignedSubject',
      'documentSignedForTeamSubject',
      'documentSignedEmail',
      'documentSignedEmailFrom',
      'documentReceivedEmail',
      'documentReceivedEmailFromName',
      'overrideSignedEmailSender',
      'overrideReceivedEmailSender',
      'overrideSignedForTeamEmailSender',
      'documentSignedForTeamEmail',
      'documentSignedForTeamEmailFromName',
      'documentSignedEmailFromName',
      'enableAutoSentInvoice',
      'enableDirectPayment',
      'ccRecipients',
      'enableDocumentRedirectUrl',
      'documentRedirectUrl',
      'documentRedirectType',
      'documentExpiryReminderSubject',
      'documentExpiryReminderTemplateId',
      'documentExpiryDays',
      'enableDocumentExpiryReminderNotification',
      'documentExpiryReminderDefaultTemplateId',
      'enableDocumentExpiry',
      'enableDocumentViewedNotification',
      'documentViewedSubject',
      'documentViewedTemplateId',
      'enableDocumentSignatureDraw',
      'enableDocumentSignatureType',
      'enableDocumentReceiveNotificationViaEmail',
      'enableDocumentReceiveNotificationViaSMS',
      'enableDocumentSignedNotificationViaEmail',
      'enableDocumentSignedNotificationViaSMS',
      'documentReceivedSMSTemplateId',
      'documentSignedSMSTemplateId',
      '_id',
    ])
  }
  const fetchCustomNotificationSetting = async () => {
    try {
      loading.value = true
      if (!locationId) return
      const { data } =
        await CustomNotificationService.getCustomNotificationSetting({
          locationId,
        })
      notificationSetting.value = sanitizeSettingsData(
        data
      ) as unknown as IProposalCustomerCustomNotificationSettings
    } catch (err) {
      console.error(err)
      error.value = err
    } finally {
      loading.value = false
    }
  }

  const updateCustomNotificationSettings = async (
    params: Partial<IProposalCustomerCustomNotificationSettings>
  ) => {
    try {
      loading.value = true
      if (!locationId) return
      const { data } =
        await CustomNotificationService.updateCustomNotificationSetting({
          locationId,
          ...notificationSetting.value,
          ...params,
        })
      notificationSetting.value = sanitizeSettingsData(
        data
      ) as unknown as IProposalCustomerCustomNotificationSettings
      handleInfo('Successfully saved')
    } catch (err) {
      console.error(err)
      error.value = err
      handleError(err)
    } finally {
      loading.value = false
    }
  }
  return {
    loading,
    error,
    notificationSetting,
    fetchCustomNotificationSetting,
    updateCustomNotificationSettings,
  }
}
